"use client";

import { useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent } from "@/components/ui/card";
import { Container } from "@/components/shared/container";
import { AnimateOnScroll } from "@/components/shared/animate-on-scroll";

// Wycięto slider „Średni czas wizyty" — był używany tylko do liczenia „dodatkowych wizyt",
// które usuwamy z outputu (audyt § 8: fałszywy positive — klinika dorobi tylko jeśli ma walk-in queue).
export const SLIDERS = {
  visitsPerDay: { label: "Liczba wizyt dziennie", min: 20, max: 300, step: 10, default: 80 },
  hourlyRate: { label: "Stawka godzinowa (PLN)", min: 50, max: 300, step: 10, default: 100 },
};

function SliderControl({
  label,
  value,
  onChange,
  min,
  max,
  step,
  unit,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  unit?: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">{label}</label>
        <span className="text-sm font-bold text-primary tabular-nums">
          {value}
          {unit ? ` ${unit}` : ""}
        </span>
      </div>
      <Slider
        value={[value]}
        onValueChange={([v]) => onChange(v)}
        min={min}
        max={max}
        step={step}
        className="w-full"
      />
    </div>
  );
}

function formatHoursPL(value: number) {
  return value.toFixed(1).replace(".", ",");
}

export function RoiStripe() {
  const [visitsPerDay, setVisitsPerDay] = useState(SLIDERS.visitsPerDay.default);
  const [hourlyRate, setHourlyRate] = useState(SLIDERS.hourlyRate.default);

  const savedMinutesPerVisit = 2;
  const savedMinutesPerDay = visitsPerDay * savedMinutesPerVisit;
  const savedHoursPerDay = savedMinutesPerDay / 60;
  const dailySavings = savedHoursPerDay * hourlyRate;
  const monthlySavings = dailySavings * 20;
  const yearlySavings = monthlySavings * 12;

  return (
    <section id="kalkulator" className="py-16 md:py-20">
      <Container>
        <AnimateOnScroll className="text-center max-w-3xl mx-auto">
          <p className="text-sm font-medium tracking-wide text-primary uppercase mb-3">
            Twoja oszczędność
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
            Średnio 2 minuty oszczędności na każdej wizycie. Policz, ile to daje
            w Twojej placówce.
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Dostosuj parametry do swojej placówki i zobacz konkretne liczby
          </p>
        </AnimateOnScroll>

        <AnimateOnScroll delay={0.1} className="mt-12 max-w-4xl mx-auto">
          <Card className="border border-border/60 !py-0 overflow-hidden">
            <CardContent className="p-6 md:p-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div className="flex flex-col gap-8 order-2 md:order-1">
                  <SliderControl
                    label={SLIDERS.visitsPerDay.label}
                    value={visitsPerDay}
                    onChange={setVisitsPerDay}
                    min={SLIDERS.visitsPerDay.min}
                    max={SLIDERS.visitsPerDay.max}
                    step={SLIDERS.visitsPerDay.step}
                  />
                  <SliderControl
                    label={SLIDERS.hourlyRate.label}
                    value={hourlyRate}
                    onChange={setHourlyRate}
                    min={SLIDERS.hourlyRate.min}
                    max={SLIDERS.hourlyRate.max}
                    step={SLIDERS.hourlyRate.step}
                    unit="zł"
                  />
                </div>

                <div className="flex flex-col items-center justify-center text-center bg-muted/50 rounded-xl p-8 order-1 md:order-2">
                  <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Oszczędzasz miesięcznie
                  </span>
                  <span className="mt-2 text-5xl md:text-6xl font-bold text-primary tabular-nums">
                    {Math.round(monthlySavings).toLocaleString("pl-PL")} zł
                  </span>
                  <span className="mt-4 text-sm text-muted-foreground">
                    Co daje rocznie:{" "}
                    <strong className="text-foreground">
                      {Math.round(yearlySavings).toLocaleString("pl-PL")} zł
                    </strong>
                  </span>
                  <div className="mt-6 flex flex-col gap-1 text-sm text-muted-foreground">
                    <span>
                      Zaoszczędzony czas dziennie:{" "}
                      <strong className="text-foreground">
                        {formatHoursPL(savedHoursPerDay)} h
                      </strong>
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </AnimateOnScroll>
      </Container>
    </section>
  );
}
